import React from "react";
import {
  Typography,
  FormControl,
  Select as MUISelect,
  MenuItem,
  Box,
  IconButton,
} from "@mui/material";
import {
  Star as StarIcon,
  SentimentVerySatisfied as SentimentVerySatisfiedIcon,
  Balance as BalanceIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  AutoFixHigh as AutoFixHighIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import DeleteIcon from "@mui/icons-material/DeleteOutline";

const AssessmentStyleSelector = ({
  assessmentStyle,
  setAssessmentStyle,
  customAssessmentStyles,
  setIsCreateStyleDialogOpen,
  handleDeleteStyle,
}) => {
  const getAllAssessmentOptions = () => {
    const standardOptions = [
      {
        value: "extra_encouraging",
        label: "Extra uppmuntrande",
        subtitle: "Mycket motiverande & självförtroendehöjande",
        isStandard: true,
        icon: (
          <SentimentVerySatisfiedIcon
            sx={{ fontSize: "1.4rem", mr: 1.7, color: "text.secondary" }}
          />
        ),
      },
      {
        value: "encouraging",
        label: "Uppmuntrande",
        subtitle: "Fokuserar på styrkor & konstruktiv feedback",
        isStandard: true,
        icon: (
          <SentimentSatisfiedAltIcon
            sx={{ fontSize: "1.4rem", mr: 1.7, color: "text.secondary" }}
          />
        ),
      },
      {
        value: "balanced",
        label: "Balanserad",
        subtitle: "Objektiv bedömning enligt vald matris",
        isStandard: true,
        icon: (
          <BalanceIcon
            sx={{ fontSize: "1.4rem", mr: 1.7, color: "text.secondary" }}
          />
        ),
      },
      {
        value: "critical",
        label: "Kritisk",
        subtitle: "Noggrann granskning med höga krav",
        isStandard: true,
        icon: (
          <SearchIcon
            sx={{ fontSize: "1.4rem", mr: 1.7, color: "text.secondary" }}
          />
        ),
      },
    ];

    const customOptions = customAssessmentStyles.map((style) => ({
      value: style.id,
      label: style.name,
      subtitle:
        style.description && style.description.length > 30
          ? style.description.substring(0, 30) + "..."
          : style.description,
      isStandard: false,
      icon: (
        <FiberManualRecordIcon
          sx={{ fontSize: "1.4rem", mr: 1.7, color: "text.secondary" }}
        />
      ),
    }));

    return { standardOptions, customOptions };
  };

  const { standardOptions, customOptions } = getAllAssessmentOptions();

  return (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
        Bedömningsstil:
      </Typography>

      <FormControl fullWidth>
        <MUISelect
          value={assessmentStyle}
          onChange={(e) => {
            if (e.target.value !== "create") {
              setAssessmentStyle(e.target.value);
            }
          }}
          displayEmpty
          renderValue={(selected) => {
            const allOptions = [...customOptions, ...standardOptions];
            const option = allOptions.find((opt) => opt.value === selected);
            return option ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {option.icon}
                {option.label}
              </Box>
            ) : (
              "Välj bedömningsstil"
            );
          }}
        >
          {/* Custom options section first (only if custom options exist) */}
          {customOptions.length > 0 && [
            <Box
              key="custom-header"
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 1,
                  display: "block",
                  fontWeight: 600,
                  bgcolor: (theme) => theme.palette.background.default + "90",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.75rem",
                }}
              >
                Mina egna stilar
              </Typography>
            </Box>,
            ...customOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    {option.icon}
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.9rem",
                        }}
                      >
                        {option.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.85rem" }}
                      >
                        {option.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteStyle(option.value, e)}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        color: "#EF4444",
                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </MenuItem>
            )),
            // Divider between custom and standard options
            <Box
              key="standard-divider"
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 1,
                  display: "block",
                  fontWeight: 600,
                  bgcolor: (theme) => theme.palette.background.default + "90",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.75rem",
                }}
              >
                Standardstilar
              </Typography>
            </Box>,
          ]}

          {/* Standard options */}
          {standardOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  {option.icon}
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      {option.subtitle}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </MenuItem>
          ))}

          {/* Create new option at the bottom */}
          <MenuItem
            key="create"
            value="create"
            onClick={(e) => {
              e.preventDefault();
              setIsCreateStyleDialogOpen(true);
            }}
            sx={{
              borderTop: "1px solid",
              pt: 2,
              pb: 1,
              borderColor: "divider",
              color: "primary.main",
              fontWeight: "medium",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AddIcon sx={{ fontSize: "1.2rem", mr: 1 }} />
              Skapa ny bedömningsstil
            </Box>
          </MenuItem>
        </MUISelect>
      </FormControl>
    </>
  );
};

export default AssessmentStyleSelector;
